import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowUp } from 'lucide-react';
import { Button } from '../components/ui/button';

const TermsConditions = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center space-x-2">
              <Shield className="h-8 w-8 text-blue-700" />
              <span className="text-2xl font-bold text-gray-900">trademark website name</span>
            </Link>
            <Link to="/">
              <Button className="bg-blue-700 hover:bg-blue-800 text-white">
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-8">Terms & Conditions</h1>
          
          <div className="prose prose-lg max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Agreement to Terms</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Welcome to trademark website name. These Terms and Conditions ("Terms") govern your use of our website and trademark registration services. By accessing our website or using our services, you agree to be bound by these Terms and our Privacy Policy.
              </p>
              <p className="text-gray-700 leading-relaxed">
                If you do not agree with any part of these Terms, you must not use our website or services. We reserve the right to modify these Terms at any time, and your continued use of our services after such modifications constitutes acceptance of the updated Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Service Description</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                trademark website name provides comprehensive trademark registration services, including trademark searches, application preparation and filing, USPTO communication management, office action responses, and trademark monitoring. Our services are designed to assist clients in protecting their intellectual property rights through federal trademark registration.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                We are not a law firm, but we work with experienced trademark attorneys and legal professionals to ensure your trademark applications receive expert attention. Our services do not constitute legal advice, and we recommend consulting with a licensed attorney for complex legal matters.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Eligibility and Account Registration</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                To use our services, you must be at least 18 years old and have the legal capacity to enter into binding contracts. By registering for our services, you represent and warrant that all information you provide is accurate, current, and complete.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must notify us immediately of any unauthorized use of your account or any other breach of security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Service Fees and Payment</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our service fees are clearly outlined in our pricing packages. These fees cover our professional services and do not include government filing fees charged by the USPTO, which are paid directly to the government and are non-refundable.
              </p>
              
              <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6">Payment Terms</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>All fees must be paid in full before we begin work on your trademark application</li>
                <li>Payments are processed securely through third-party payment processors</li>
                <li>All fees are quoted in U.S. dollars</li>
                <li>We accept major credit cards, debit cards, and electronic bank transfers</li>
                <li>Additional fees may apply for services beyond the scope of your selected package</li>
              </ul>

              <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6">Refund Policy</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our service fees are generally non-refundable once work has commenced on your trademark application. However, we may provide refunds on a case-by-case basis for services not yet rendered. Government filing fees paid to the USPTO are never refundable, regardless of the outcome of your application.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Client Responsibilities</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                To ensure successful trademark registration, clients are responsible for:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Providing accurate and complete information about their trademark and business</li>
                <li>Responding promptly to requests for additional information or documentation</li>
                <li>Reviewing and approving all documents before filing with the USPTO</li>
                <li>Ensuring they have the legal right to use and register the proposed trademark</li>
                <li>Maintaining actual use of the trademark in commerce as required by law</li>
                <li>Complying with all USPTO requirements and deadlines</li>
                <li>Notifying us of any changes to contact information or business details</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">No Guarantee of Registration</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                While we strive to maximize your chances of successful trademark registration, we cannot guarantee that the USPTO will approve your application. The USPTO makes independent decisions based on federal trademark law and regulations, and applications may be refused for various reasons including likelihood of confusion with existing marks, descriptiveness, or genericness.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                We are not responsible for USPTO refusals or delays in the examination process. Our high success rate reflects our expertise and attention to detail, but ultimate approval is at the discretion of the USPTO examining attorney.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Intellectual Property Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                All content on our website, including text, graphics, logos, images, and software, is the property of trademark website name or its content suppliers and is protected by U.S. and international copyright and trademark laws. You may not reproduce, distribute, modify, or create derivative works from our website content without our express written permission.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Your trademark and related materials remain your property. By using our services, you grant us a limited license to use your trademark information solely for the purpose of providing our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Confidentiality</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We maintain strict confidentiality regarding your trademark information and business details. However, you acknowledge that certain information will be disclosed to the USPTO as part of the public trademark application process and will become part of the public record.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                We will not disclose your confidential information to third parties except as necessary to provide our services, comply with legal obligations, or with your express consent.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                To the fullest extent permitted by law, trademark website name and its officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, use, goodwill, or other intangible losses, resulting from:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Your use of or inability to use our services</li>
                <li>Any unauthorized access to or use of our servers and/or any personal information stored therein</li>
                <li>Any interruption or cessation of transmission to or from our website</li>
                <li>Any bugs, viruses, or similar elements that may be transmitted through our website</li>
                <li>Any errors or omissions in any content or for any loss or damage incurred as a result of the use of any content</li>
                <li>USPTO rejection or refusal of your trademark application</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our total liability to you for all claims arising from or related to our services shall not exceed the amount you paid us for services in the twelve months preceding the claim.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Indemnification</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You agree to indemnify, defend, and hold harmless trademark website name and its officers, directors, employees, agents, and affiliates from and against any claims, liabilities, damages, losses, and expenses, including reasonable attorneys' fees, arising out of or in any way connected with:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Your use of our services</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any third-party rights, including intellectual property rights</li>
                <li>Any false or misleading information you provide to us</li>
                <li>Your actual use or non-use of the trademark in commerce</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Termination</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We reserve the right to suspend or terminate your access to our services at any time, without prior notice, for conduct that we believe violates these Terms, is harmful to other users, or is otherwise inappropriate or unlawful.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                You may terminate your relationship with us at any time by discontinuing use of our services. However, any fees paid for services already rendered are non-refundable.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Dispute Resolution and Arbitration</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Any dispute, controversy, or claim arising out of or relating to these Terms or our services shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association. The arbitration shall take place in Los Angeles County, California.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                You agree to waive any right to a jury trial or to participate in a class action lawsuit. The arbitrator's decision shall be final and binding, and judgment may be entered in any court of competent jurisdiction.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Governing Law</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                These Terms shall be governed by and construed in accordance with the laws of the State of California and the federal laws of the United States, without regard to conflict of law principles. Any legal action or proceeding relating to these Terms shall be brought exclusively in the federal or state courts located in Los Angeles County, California.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Severability</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If any provision of these Terms is found to be invalid, illegal, or unenforceable, the remaining provisions shall continue in full force and effect. The invalid provision shall be modified to the minimum extent necessary to make it valid and enforceable.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Entire Agreement</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                These Terms, together with our Privacy Policy and any other agreements referenced herein, constitute the entire agreement between you and trademark website name regarding the use of our services and supersede all prior or contemporaneous communications, whether electronic, oral, or written.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to Terms</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We reserve the right to modify these Terms at any time. We will notify you of material changes by posting the updated Terms on our website and updating the effective date. Your continued use of our services after such changes constitutes acceptance of the modified Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions about these Terms and Conditions, please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-2"><strong>trademark website name</strong></p>
                <p className="text-gray-700 mb-2">500 N Brand Blvd #2000, Glendale, CA 91203</p>
                <p className="text-gray-700 mb-2">Phone: +1 (123) 123-4567</p>
                <p className="text-gray-700">Email: info@trademarkwebsitename.com</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Acknowledgment</h2>
              <p className="text-gray-700 leading-relaxed">
                By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions. You also acknowledge that you have read and understood our Privacy Policy.
              </p>
            </section>
          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-blue-700 hover:bg-blue-800 text-white p-3 rounded-full shadow-lg transition-all duration-300 z-50"
        >
          <ArrowUp className="h-6 w-6" />
        </button>
      )}
    </div>
  );
};

export default TermsConditions;
